/**
 * SUP-ADMIN-UI-01 — bug-report detail drawer.
 *
 * Renders the full `BugReport` for platform-admin triage: lifecycle header,
 * reporter, description, traceback, the free-form `systemInfo` (AI2
 * platform_info + drive_info) and `logGroups` as readable pretty-JSON, and the
 * attachment with a "Download zip" action (the short-TTL read SAS — disabled
 * until the attachment is `verified`).
 *
 * Actions: inline status `<select>`, assignee user id, and resolution note →
 * `PATCH`, with refetch-on-success (the mutation seeds the detail cache and
 * invalidates the list). Per the minimal progress-indication convention there
 * is ONE inline saving/error surface, not per-control spinners.
 */
import { useEffect, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  PageHeading,
  compactFieldSx,
  compactMenuProps,
  fieldLabelSx,
} from "@aeriosiq/ui";
import { useBugReportQuery, usePatchBugReportMutation } from "./hooks.js";
import {
  BUG_REPORT_STATUSES,
  type BugReport,
  type BugReportPatch,
  type BugReportStatus,
} from "./types.js";

interface Props {
  bugReportId: string | null;
  onClose: () => void;
}

export function BugReportDetailDrawer({
  bugReportId,
  onClose,
}: Props): ReactElement {
  const open = bugReportId != null;
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 560 } } }}
    >
      {bugReportId != null ? (
        <DetailBody bugReportId={bugReportId} onClose={onClose} />
      ) : null}
    </Drawer>
  );
}

function DetailBody({
  bugReportId,
  onClose,
}: {
  bugReportId: string;
  onClose: () => void;
}): ReactElement {
  const { data, isPending, isError } = useBugReportQuery(bugReportId);

  return (
    <Box sx={{ p: 3 }} role="region" aria-label="Bug report detail">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <PageHeading>Bug report</PageHeading>
        <IconButton aria-label="Close detail" onClick={onClose} size="small">
          ✕
        </IconButton>
      </Stack>

      {isPending ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : isError ? (
        <Alert severity="error">Couldn&apos;t load this report.</Alert>
      ) : (
        <DetailContent report={data} />
      )}
    </Box>
  );
}

function DetailContent({ report }: { report: BugReport }): ReactElement {
  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {report.title}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
          <Chip size="small" label={report.kind} />
          <Chip size="small" label={report.severity} color="warning" />
          <Chip size="small" label={report.status} color="info" />
          <Chip size="small" label={report.sourceApp} variant="outlined" />
        </Stack>
      </Box>

      <MetaGrid report={report} />

      <Divider />

      <Field label="Description">
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {report.description}
        </Typography>
      </Field>

      {report.traceback ? (
        <Field label="Traceback">
          <Pre>{report.traceback}</Pre>
        </Field>
      ) : null}

      {report.systemInfo ? (
        <Field label="System info">
          <Pre>{prettyJson(report.systemInfo)}</Pre>
        </Field>
      ) : null}

      {report.logGroups && report.logGroups.length > 0 ? (
        <Field label="Log groups">
          <Pre>{prettyJson(report.logGroups)}</Pre>
        </Field>
      ) : null}

      <Attachments report={report} />

      <Divider />

      <TriageActions report={report} />
    </Stack>
  );
}

function MetaGrid({ report }: { report: BugReport }): ReactElement {
  const rows: [string, string][] = [
    ["Tenant", report.tenantId],
    ["Reporter", report.reporter.email],
    ["App version", report.appVersion],
    ["Created", new Date(report.createdAt).toLocaleString()],
    ["Updated", new Date(report.updatedAt).toLocaleString()],
    ["Assignee", report.assigneeUserId ?? "—"],
  ];
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "max-content 1fr",
        rowGap: 0.5,
        columnGap: 2,
      }}
    >
      {rows.map(([k, v]) => (
        <Box key={k} sx={{ display: "contents" }}>
          <Typography variant="caption" sx={fieldLabelSx}>
            {k}
          </Typography>
          <Typography
            variant="body2"
            sx={{ wordBreak: "break-word", fontVariantNumeric: "tabular-nums" }}
          >
            {v}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function Attachments({ report }: { report: BugReport }): ReactElement {
  if (report.attachments.length === 0) {
    return (
      <Field label="Attachment">
        <Typography variant="body2" color="text.secondary">
          No attachment.
        </Typography>
      </Field>
    );
  }
  return (
    <Field label="Attachment">
      <Stack spacing={1}>
        {report.attachments.map((a) => {
          const ready = a.state === "verified" && a.downloadSasUrl != null;
          return (
            <Stack
              key={a.attachmentId}
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ flexWrap: "wrap" }}
            >
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                {a.filename}
              </Typography>
              <Chip size="small" label={a.state} variant="outlined" />
              {ready && a.downloadSasUrl != null ? (
                // The SAS URL points at Azure blob storage; let the browser
                // download directly (bytes never traverse the gateway).
                <Button
                  size="small"
                  variant="outlined"
                  component="a"
                  href={a.downloadSasUrl}
                  rel="noopener noreferrer"
                >
                  Download zip
                </Button>
              ) : (
                <Button size="small" variant="outlined" disabled>
                  Download zip
                </Button>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Field>
  );
}

function TriageActions({ report }: { report: BugReport }): ReactElement {
  const patch = usePatchBugReportMutation(report.bugReportId);
  const [status, setStatus] = useState<BugReportStatus>(report.status);
  const [assignee, setAssignee] = useState(report.assigneeUserId ?? "");
  const [note, setNote] = useState(report.resolutionNote ?? "");

  // Re-sync local form state when the underlying report changes (e.g. a
  // different row opened into the same drawer, or a refetch landed).
  useEffect(() => {
    setStatus(report.status);
    setAssignee(report.assigneeUserId ?? "");
    setNote(report.resolutionNote ?? "");
  }, [
    report.bugReportId,
    report.status,
    report.assigneeUserId,
    report.resolutionNote,
  ]);

  const dirty =
    status !== report.status ||
    assignee !== (report.assigneeUserId ?? "") ||
    note !== (report.resolutionNote ?? "");

  function save(): void {
    const body: BugReportPatch = {};
    if (status !== report.status) body.status = status;
    if (assignee !== (report.assigneeUserId ?? "")) {
      body.assigneeUserId = assignee.trim() === "" ? null : assignee.trim();
    }
    if (note !== (report.resolutionNote ?? "")) {
      body.resolutionNote = note.trim() === "" ? null : note;
    }
    patch.mutate(body);
  }

  return (
    <Stack spacing={2} component="form" aria-label="Triage actions">
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Triage
      </Typography>

      <TextField
        select
        label="Status"
        size="small"
        value={status}
        onChange={(e) => {
          setStatus(e.target.value as BugReportStatus);
        }}
        sx={compactFieldSx}
        slotProps={{ select: { MenuProps: compactMenuProps } }}
      >
        {BUG_REPORT_STATUSES.map((s) => (
          <MenuItem key={s} value={s}>
            {s}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="Assignee user id"
        size="small"
        placeholder="(unassigned)"
        value={assignee}
        onChange={(e) => {
          setAssignee(e.target.value);
        }}
        sx={compactFieldSx}
      />

      <TextField
        label="Resolution note"
        size="small"
        multiline
        minRows={2}
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
        }}
        sx={compactFieldSx}
      />

      {patch.isError ? (
        <Alert severity="error">Couldn&apos;t save the change.</Alert>
      ) : null}
      {patch.isSuccess && !dirty ? (
        <Alert severity="success">Saved.</Alert>
      ) : null}

      <Button
        variant="contained"
        onClick={save}
        disabled={!dirty || patch.isPending}
      >
        {patch.isPending ? "Saving…" : "Save changes"}
      </Button>
    </Stack>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactElement | ReactElement[];
}): ReactElement {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ ...fieldLabelSx, display: "block", mb: 0.5 }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function Pre({ children }: { children: string }): ReactElement {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        borderRadius: 1,
        bgcolor: (t) =>
          t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "grey.100",
        fontSize: 12,
        lineHeight: 1.5,
        overflowX: "auto",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        maxHeight: 320,
      }}
    >
      {children}
    </Box>
  );
}

function prettyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
